/**
 * MICROSCOPIC / OPTICAL SCALE — the aerial image.
 *
 * Lithography is the module where the proposal's central claim is easiest to
 * see: a technician sets two numbers, dose and focus, and a critical dimension
 * comes out. This file is the first half of why.
 *
 * The image of a dense line/space grating is built from its diffraction orders.
 * A grating of pitch P sends light into orders at sin(theta_m) = m*lambda/P;
 * the lens catches an order only if that angle is inside its numerical
 * aperture. Catching just the zeroth order gives a uniform grey with no image
 * at all - which is why there is a hard resolution limit, and why it moves when
 * you change wavelength or NA rather than when you try harder.
 *
 * Defocus is the interesting part. Moving the wafer off the focal plane makes
 * each order travel a slightly different distance along the optical axis, so
 * the +/-1 orders arrive phase-shifted relative to the zeroth, and the
 * interference term picks up a cos of that shift. Contrast therefore falls,
 * passes through zero, and can invert. That single term is the origin of the
 * Bossung curves a lithographer reads every day - they are not drawn here,
 * they fall out.
 *
 * THE PHASE IS COMPUTED EXACTLY - see `defocusPhaseRad`, which is where the
 * argument for that lives. The textbook expression, pi z lambda / P^2, is a
 * small-angle approximation written for a dry lens, and an immersion scanner
 * violates both halves of that: its orders travel at fifty degrees inside the
 * water and the water's index is in the answer. It overstates the defocus
 * phase by a fifth at NA 1.35, which is a fifth off every depth of focus this
 * module quotes. The three-beam wave scene next door measures the phase off a
 * propagating field and agrees with the exact form; see sim/litho/focus.ts.
 */

import { clamp } from '../../core/math'

export interface Illumination {
  /** Exposure wavelength [m]. 193e-9 for immersion DUV, 13.5e-9 for EUV. */
  wavelengthM: number
  /** Numerical aperture. */
  na: number
  /** Partial coherence factor, 0 = coherent, ~0.9 = very incoherent. */
  sigma: number
  /**
   * Refractive index of the medium between the last lens element and the
   * resist: water for an immersion scanner, vacuum for EUV.
   *
   * Not cosmetic, and not inferable. It sets the angle the orders actually
   * travel at, since NA = n sin(theta), and it therefore sets the defocus
   * phase - which is where it was missing before, costing a fifth of every
   * immersion depth of focus. It is also the only reason an NA above 1
   * exists at all: a sine cannot exceed one, so a dry lens has no angle
   * behind NA 1.35 however it is built.
   */
  mediumIndex: number
  /**
   * Reduction magnification, across the slit and along the scan.
   *
   * Four everywhere until High-NA. Getting to NA 0.55 in a reflective system
   * means the chief ray hits the mask at a steeper angle, and past about
   * six degrees the multilayer coating stops reflecting and shadowing ruins
   * the image. The fix was to make the optics ANAMORPHIC - 8x demagnification
   * in one direction and 4x in the other - which keeps the mask-side angle
   * manageable at the cost of halving the field.
   */
  magX: number
  magY: number
  /** Exposure field at the wafer [mm]. */
  fieldXMm: number
  fieldYMm: number
}

export const DUV_IMMERSION: Illumination = {
  wavelengthM: 193e-9,
  na: 1.35,
  sigma: 0.85,
  // Water at 193 nm, which is 1.44 rather than the 1.33 of visible light.
  mediumIndex: 1.44,
  magX: 4,
  magY: 4,
  fieldXMm: 26,
  fieldYMm: 33,
}

export const EUV: Illumination = {
  wavelengthM: 13.5e-9,
  na: 0.33,
  sigma: 0.6,
  // Vacuum: nothing is transparent at 13.5 nm, which is also why EUV
  // cannot reach an NA above 1 by the trick immersion uses.
  mediumIndex: 1,
  magX: 4,
  magY: 4,
  fieldXMm: 26,
  fieldYMm: 33,
}

/**
 * High-NA EUV. NA 0.55, anamorphic, half field.
 *
 * The trade is not subtle and it is not free. Resolution goes as lambda/NA
 * and depth of focus goes as lambda/NA^2, so raising NA from 0.33 to 0.55
 * buys 1.67x in resolution and costs 2.8x in focus budget - on a tool that
 * had barely any to begin with. And the anamorphic optics that make the
 * higher NA possible at all halve the field, so a die that used to take one
 * exposure now takes two and has to be stitched.
 *
 * A student who reads "High-NA is better" and stops there has learned the
 * marketing. The competency is knowing what it costs.
 */
export const HIGH_NA_EUV: Illumination = {
  wavelengthM: 13.5e-9,
  na: 0.55,
  sigma: 0.6,
  mediumIndex: 1,
  magX: 4,
  magY: 8,
  fieldXMm: 26,
  fieldYMm: 16.5,
}

/**
 * Rayleigh depth of focus [m].
 *
 * The second Rayleigh criterion, and the one that decides whether a tool can
 * print on a real wafer. A wafer that is out of focus by z puts the marginal
 * ray a distance n z (1 - cos theta) of extra path out of step with the axial
 * one, and the criterion is that the tool stays in focus while that stays
 * under a fixed fraction of a wave. Solved for z, with sin(theta) = NA / n:
 *
 *     DOF = k2 * lambda / (2 n (1 - cos theta))
 *
 * WHY NOT k2 * lambda / NA^2, which is what this used to be and what every
 * table prints. That is the expression above with the cosine expanded for
 * small angles AND the medium taken to be vacuum - and at hyper-NA both
 * corrections are large. They also very nearly CANCEL, which is why the old
 * number looked reasonable and why fixing only one of them would have been
 * worse than fixing neither: at NA 1.35 in water, putting the index in and
 * leaving the angles paraxial gives 76 nm where the truth is 51, against the
 * 53 the uncorrected form happened to land on.
 *
 * So the square law is a LOW-NA statement. Depth of focus falls at least as
 * fast as NA squared and faster once the angles stop being small: halving NA
 * from 1.35 gives back 5.6x the focus budget, not 4x. That makes the module's
 * central bargain worse than the textbook version of it, not better, which is
 * the honest form of the argument for CMP-11.
 */
export const depthOfFocusM = (ill: Illumination, k2 = 0.6): number => {
  const { wavelengthM: lambda, na, mediumIndex: n } = ill
  // An NA at or above the medium's index has no angle behind it - a sine
  // cannot exceed one - so the expression saturates at its floor rather than
  // returning a complex cosine. No real source reaches this; the clamp is
  // here so that a hypothetical one in a sweep does not poison an average.
  const sinTheta = clamp(na / n, 0, 1)
  const cosTheta = Math.sqrt(1 - sinTheta * sinTheta)
  return (k2 * lambda) / (2 * n * (1 - cosTheta))
}

/**
 * Smallest resolvable half-pitch [m]: k1 * lambda / NA, at the k1 = 0.25
 * single-exposure physical limit.
 */
export const resolutionLimitM = (ill: Illumination, k1 = 0.25): number =>
  (k1 * ill.wavelengthM) / ill.na

/**
 * Exposures needed to cover one die, and whether they have to be stitched.
 *
 * A half-field tool does not simply run at half the throughput - a die larger
 * than the field has to be printed in pieces and joined, and the join is an
 * overlay problem in the middle of the product rather than at its edge.
 */
export function fieldsPerDie(
  ill: Illumination,
  dieXMm: number,
  dieYMm: number,
): { fields: number; stitched: boolean } {
  const nx = Math.ceil(dieXMm / ill.fieldXMm)
  const ny = Math.ceil(dieYMm / ill.fieldYMm)
  return { fields: nx * ny, stitched: nx * ny > 1 }
}

/**
 * The defocus phase between the zeroth order and a first order [radians].
 *
 * Every order leaves the pupil at its own angle, so over a wafer displacement
 * z each advances by its own amount along the axis - z times the DEPTH
 * component of its wavevector, k n cos(theta). The image is the interference
 * between the zeroth order and the first ones, so what matters is the
 * difference:
 *
 *     dphi = k0 n z [cos(theta_0) - cos(theta_1)]
 *
 * and that is what this returns, with the cosines computed rather than
 * expanded.
 *
 * WHY NOT pi z lambda / P^2, which is what every textbook prints and what
 * this module used to use. That expression is the above with two
 * simplifications folded in: cos(theta) replaced by its small-angle value,
 * and the medium taken to be vacuum. Both are fine for a dry lens printing
 * relaxed pitches and neither survives immersion. At NA 1.35 and a 180 nm
 * pitch the orders travel at forty-eight degrees inside the water, where the
 * small-angle value of the cosine is out by five per cent, and the water's
 * index of 1.44 is missing from the expression altogether. The two errors
 * pull opposite ways and do not cancel: the net is that the textbook form
 * OVERSTATES the phase by twenty per cent, so every depth of focus computed
 * from it was a fifth too small. At EUV, where the medium is vacuum and the
 * angles are gentler, the same expression is within four per cent - which is
 * why the error hid.
 *
 * The three-beam wave scene measures this phase off a propagating field and
 * lands within half a per cent of the exact form and nowhere near the
 * paraxial one; see sim/litho/focus.ts and scripts/focus-check.ts.
 *
 * @param pupilOffset where the illuminating source point sits in the pupil,
 *   in units of NA. Zero is on-axis, which is the phase the image's own
 *   cos(dphi) uses; the source's EXTENT is what smears it, and that is the
 *   envelope in `aerialImage`.
 */
export function defocusPhaseRad(
  pitchM: number,
  defocusM: number,
  ill: Illumination,
  pupilOffset = 0,
): number {
  const { wavelengthM: lambda, na, mediumIndex: n } = ill
  if (!(pitchM > 0) || defocusM === 0) return 0
  // A pupil position u (in units of NA) is the direction sin(theta) = u NA / n.
  const cosAt = (u: number): number => {
    const s = (u * na) / n
    return Math.sqrt(Math.max(0, 1 - s * s))
  }
  // The first order sits f further out in the pupil than whatever illuminated
  // it, where f is the diffraction angle in the same normalised units.
  const f = lambda / (pitchM * na)
  const k = (2 * Math.PI * n) / lambda
  return k * defocusM * (cosAt(pupilOffset) - cosAt(pupilOffset + f))
}

export interface AerialImage {
  /** Intensity sampled across one pitch, normalised so the mean is 1. */
  intensity: Float64Array
  /** Position of each sample [m], spanning one pitch centred on a line. */
  x: Float64Array
  /** Image contrast (Imax - Imin) / (Imax + Imin). */
  contrast: number
  /**
   * Normalised image log slope at the nominal edge - the single best predictor
   * of how much a resist edge will wander. Higher is better; below about 2 the
   * process is fragile no matter how good the resist.
   */
  nils: number
  /** k1 = half-pitch * NA / lambda. Below 0.25 is physically impossible. */
  k1: number
  /** True when the lens cannot capture the first diffraction order at all. */
  unresolved: boolean
}

/**
 * Compute the aerial image of a dense line/space grating.
 *
 * Three-beam imaging: the zeroth order and the two first orders. For a binary
 * grating at 50% duty the amplitudes are the Fourier coefficients of a square
 * wave - a0 = 1/2 and a1 = 1/pi.
 */
export function aerialImage(
  pitchM: number,
  defocusM: number,
  ill: Illumination,
  samples = 256,
): AerialImage {
  const { wavelengthM: lambda, na, sigma } = ill

  const k1 = (pitchM / 2) * (na / lambda)
  // The first order sits at sin(theta) = lambda / P. Partial coherence tilts
  // the illumination, so an order is still caught out to NA*(1+sigma).
  const firstOrderSin = lambda / pitchM
  const unresolved = firstOrderSin > na * (1 + sigma)

  const a0 = 0.5
  const a1 = 1 / Math.PI

  // Defocus phase between the zeroth and first orders, computed exactly -
  // see `defocusPhaseRad` for why the textbook expression is not used.
  const dphi = defocusPhaseRad(pitchM, defocusM, ill)

  // Partial coherence: each source point sees a slightly different defocus
  // phase, and averaging over the source washes the interference term out.
  // The average of cos(dphi + d) over a uniform spread of half-width W is
  // cos(dphi) * sinc(W), which is where this envelope comes from.
  //
  // The half-width is measured rather than approximated, from the same exact
  // phase evaluated at the two edges of the source. Doing it any other way
  // would leave the envelope carrying the small-angle, vacuum assumptions the
  // phase above has just been rid of - and the old closed form did not even
  // depend on the pitch, which the spread certainly does.
  const spread = Math.abs(
    (defocusPhaseRad(pitchM, defocusM, ill, sigma) -
      defocusPhaseRad(pitchM, defocusM, ill, -sigma)) /
      2,
  )
  const coherenceEnvelope = spread > 1e-9 ? Math.sin(spread) / spread : 1

  // What fraction of the source actually forms an image.
  //
  // An extended source illuminates the mask from a range of angles. A source
  // point sitting at normalised pupil position s sends the zeroth order to s
  // and the first orders to s +/- f, where f = lambda / (P * NA) in the same
  // units. That point contributes an image only if the lens catches the zeroth
  // order AND at least one first order.
  //
  // Integrated over the source disc this is a lens-overlap area, so it is
  // computed numerically rather than approximated. It is worth doing properly
  // because it is the whole reason off-axis illumination exists: as f grows
  // past 1 the only source points still forming an image are the ones far off
  // axis, which is precisely what an annular or dipole source keeps and a
  // conventional source wastes.
  const f = lambda / (pitchM * na)
  const captureFraction = sourceCapture(f, sigma)

  const modulation = unresolved
    ? 0
    : 4 * a0 * a1 * Math.cos(dphi) * coherenceEnvelope * captureFraction
  const harmonic = unresolved ? 0 : 2 * a1 * a1 * captureFraction * captureFraction

  const x = new Float64Array(samples)
  const intensity = new Float64Array(samples)
  const k = (2 * Math.PI) / pitchM

  let min = Infinity
  let max = -Infinity
  for (let i = 0; i < samples; i++) {
    const xi = (i / samples - 0.5) * pitchM
    x[i] = xi
    // |a0 + 2 a1 cos(kx) e^{i dphi}|^2, expanded.
    const v =
      a0 * a0 + 2 * a1 * a1 + harmonic * Math.cos(2 * k * xi) + modulation * Math.cos(k * xi)
    intensity[i] = Math.max(v, 0)
    if (v < min) min = v
    if (v > max) max = v
  }

  // Normalise to mean 1 so dose means the same thing at every pitch.
  let mean = 0
  for (let i = 0; i < samples; i++) mean += intensity[i]
  mean /= samples
  if (mean > 0) for (let i = 0; i < samples; i++) intensity[i] /= mean

  const contrast = max + min > 0 ? (max - min) / (max + min) : 0

  // NILS is evaluated AT THE NOMINAL EDGE - a quarter pitch from the line
  // centre - not wherever the log slope happens to be largest.
  //
  // That distinction is not pedantic. The log slope diverges wherever the
  // intensity approaches zero, so taking the maximum reports enormous values
  // for a perfectly focused image and says nothing about the edge that
  // actually gets printed. The edge is where the resist decides, so the edge is
  // where the metric belongs.
  const dx = pitchM / samples
  const edgeIndex = Math.round(samples * 0.25)
  const iHere = Math.max(intensity[edgeIndex], 1e-3)
  const slope = Math.abs(
    (intensity[(edgeIndex + 1) % samples] - intensity[(edgeIndex - 1 + samples) % samples]) /
      (2 * dx),
  )
  const nils = clamp((slope / iHere) * (pitchM / 2), 0, 50)

  return { intensity, x, contrast, nils, k1, unresolved: unresolved || contrast < 0.02 }
}

/**
 * Photons delivered per square nanometre at a given dose.
 *
 * This is the whole stochastic story in one line. An EUV photon carries about
 * fourteen times the energy of a DUV one, so the SAME dose in mJ/cm^2 arrives
 * as fourteen times fewer photons. Dose is energy; printing is counting.
 */
export function photonsPerNm2(doseMjPerCm2: number, wavelengthM: number): number {
  const PLANCK_C = 1.98644586e-25 // h*c [J m]
  const photonEnergy = PLANCK_C / wavelengthM
  // mJ/cm^2 -> J/m^2 is a factor of 10.
  const energyPerM2 = doseMjPerCm2 * 10
  const perM2 = energyPerM2 / photonEnergy
  return perM2 * 1e-18
}

/** Photon energy in electron-volts, for display. */
export function photonEnergyEv(wavelengthM: number): number {
  return 1.23984193e-6 / wavelengthM
}

/**
 * Depth of focus from the Rayleigh criterion, at the stricter constant.
 *
 * The same physics as `depthOfFocusM` and deliberately the same code - the
 * two differ only in where the criterion is drawn, which is a convention
 * rather than a fact. Keeping them as one expression is the point: when the
 * formula was wrong it was wrong in two places, and a reader comparing the
 * panel's focus budget against the harness's had no way to tell whether a
 * difference was the constant or the model.
 */
export function rayleighDof(ill: Illumination, k2 = 0.5): number {
  return depthOfFocusM(ill, k2)
}

export { clamp }

/**
 * Fraction of a conventional (disc) source that forms an image of a grating.
 *
 * f is the first-order shift in normalised pupil units, lambda / (P * NA).
 * A source point at s contributes if |s| <= 1 (its zeroth order is caught) and
 * at least one of |s +/- f| <= 1.
 *
 * Sampled on a grid rather than solved in closed form: the closed form is a
 * lens-overlap integral with several cases, and at this grid density the answer
 * is good to well under a percent, which is far finer than anything downstream
 * can use.
 */
export function sourceCapture(f: number, sigma: number): number {
  const N = 64
  let hit = 0
  let total = 0
  for (let i = 0; i < N; i++) {
    const sx = ((i + 0.5) / N - 0.5) * 2 * sigma
    for (let j = 0; j < N; j++) {
      const sy = ((j + 0.5) / N - 0.5) * 2 * sigma
      if (sx * sx + sy * sy > sigma * sigma) continue
      total++
      if (Math.hypot(sx, sy) > 1) continue // zeroth order lost
      const plus = Math.hypot(sx + f, sy) <= 1
      const minus = Math.hypot(sx - f, sy) <= 1
      if (plus || minus) hit++
    }
  }
  return total > 0 ? hit / total : 0
}
